import { jsx, jsxs } from "react/jsx-runtime";
import React__default, { useRef, useState, useEffect } from "react";
import classNames from "classnames";
import { FormOnColor, FormSize, IconSize, AnalyticsId, AVERAGE_CHARACTER_WIDTH_PX } from "./constants.js";
import { useBreakpoint, Breakpoint } from "./hooks/useBreakpoint.js";
import { useUuid } from "./hooks/useUuid.js";
import { getColor } from "./theme/currys/color.js";
import { getAriaDescribedBy } from "./utils/accessibility.js";
import { mergeRefs } from "./utils/refs.js";
import { E as ErrorWrapper } from "./ErrorWrapper.js";
import { I as Icon } from "./Icon.js";
import { a as renderLabel } from "./Label.js";
import { L as LazyIcon } from "./LazyIcon.js";
import { M as MaxCharacters } from "./MaxCharacters.js";
import styles from "./components/Input/styles.module.scss";
var InputTypes = /* @__PURE__ */ ((InputTypes2) => {
  InputTypes2["text"] = "text";
  InputTypes2["number"] = "number";
  InputTypes2["email"] = "email";
  InputTypes2["password"] = "password";
  InputTypes2["search"] = "search";
  InputTypes2["tel"] = "tel";
  InputTypes2["url"] = "url";
  InputTypes2["date"] = "date";
  InputTypes2["time"] = "time";
  return InputTypes2;
})(InputTypes || {});
const getInputMaxWidth = (characters, hasIcon, iconSize) => {
  const paddingWidth = hasIcon ? "1.5rem" : "2rem";
  const iconWidth = hasIcon ? `${iconSize}px` : "0px";
  const borderWidth = "4px";
  return `calc(${characters * AVERAGE_CHARACTER_WIDTH_PX}px + ${paddingWidth} + ${iconWidth} + ${borderWidth})`;
};
const Input = React__default.forwardRef((props, ref) => {
  const {
    className,
    defaultValue,
    placeholder,
    type = "text",
    name,
    transparent = false,
    icon,
    iconRight,
    inputId,
    inputWrapperRef,
    onColor = FormOnColor.onwhite,
    size,
    baseIncrementValue,
    label,
    error,
    errorText,
    errorTextId,
    errorWrapperClassName,
    testId,
    disabled,
    readOnly,
    autoComplete = "off",
    afterInputChildren,
    rightOfInput,
    width,
    required,
    onChange,
    onKeyDown,
    autoFocus,
    maxCharacters,
    maxText,
    inputContainerRef,
    ...rest
  } = props;
  const breakpoint = useBreakpoint();
  const inputContainerRefLocal = useRef(null);
  const inputIdState = useUuid(inputId);
  const [input, setInput] = useState(defaultValue || "");
  const [prevValue, setPrevValue] = useState(void 0);
  const numKeyPressed = useRef(false);
  const errorTextUuid = useUuid(errorTextId);
  const numRegex = /^[0-9]$/;
  useEffect(() => {
    setInput(defaultValue || "");
  }, [defaultValue]);
  const onDark = onColor === FormOnColor.ondark;
  const onBlueberry = onColor === FormOnColor.onblueberry;
  const maxCharactersExceeded = !!maxCharacters && input.toString().length > maxCharacters;
  const onError = onColor === FormOnColor.oninvalid || !!errorText || !!error || maxCharactersExceeded;
  const isLarge = size === FormSize.large;
  const isTransparent = transparent && onColor !== FormOnColor.ondark && !onError;
  const inputWrapperClass = classNames(styles["input-wrapper"], className);
  const inputContainer = classNames(styles["input-container"], {
    [styles["input-container--transparent"]]: isTransparent,
    [styles["input-container--on-blueberry"]]: onBlueberry,
    [styles["input-container--on-dark"]]: onDark,
    [styles["input-container--invalid"]]: onError,
    [styles["input-container--large"]]: isLarge,
    [styles["input-container--disabled"]]: disabled,
    [styles["input-container--with-icon"]]: icon
  });
  const inputClass = classNames(styles["input-container__input"], {
    [styles["input-container__input--large"]]: isLarge,
    [styles["input-container__input--disabled"]]: disabled
  });
  const iconColor = disabled ? getColor("neutral", 700) : getColor("black");
  const iconSize = breakpoint === Breakpoint.xs || !isLarge ? IconSize.XSmall : IconSize.Small;
  const renderIcon = () => {
    if (!icon) {
      return;
    }
    if (typeof icon === "string") {
      return /* @__PURE__ */ jsx(LazyIcon, { className: styles["input-container__input__icon"], color: iconColor, size: iconSize, iconName: icon });
    }
    return /* @__PURE__ */ jsx(Icon, { className: styles["input-container__input__icon"], color: iconColor, size: iconSize, svgIcon: icon });
  };
  const handleClick = (e) => {
    if (inputContainerRefLocal && inputContainerRefLocal.current && icon) {
      const selectedChild = iconRight ? 0 : 1;
      const input2 = inputContainerRefLocal.current.children[selectedChild];
      input2.focus();
      props.onClick && props.onClick(e);
    }
  };
  const handleChange = (e) => {
    const newValue = getIncrementValue(e);
    if (onChange) {
      onChange(e);
    }
    setInput(newValue);
    setPrevValue(newValue);
  };
  const getIncrementValue = (e) => {
    if (typeof baseIncrementValue === "undefined" || type !== "number") return e.target.value;
    const valueAsNumber = Number(e.target.value);
    if (!prevValue && !numKeyPressed.current && (valueAsNumber === 1 || valueAsNumber === -1)) {
      e.target.value = baseIncrementValue + "";
    }
    return e.target.value;
  };
  const handleKeyDown = (e) => {
    if (numRegex.test(e.key)) {
      numKeyPressed.current = true;
    }
    onKeyDown && onKeyDown(e);
  };
  const handleKeyUp = (e) => {
    if (numRegex.test(e.key)) {
      numKeyPressed.current = false;
    }
  };
  const maxWidth = width ? getInputMaxWidth(width, !!icon, iconSize) : void 0;
  return /* @__PURE__ */ jsx(ErrorWrapper, { className: errorWrapperClassName, errorText, errorTextId: errorTextUuid, children: /* @__PURE__ */ jsxs("div", { "data-testid": testId, "data-analyticsid": AnalyticsId.Input, className: inputWrapperClass, ref: inputWrapperRef, children: [
    renderLabel(label, inputIdState, onColor),
    /* @__PURE__ */ jsxs("div", { className: styles["content-wrapper"], children: [
      /* @__PURE__ */ jsxs(
        "div",
        {
          onClick: handleClick,
          ref: mergeRefs([inputContainerRefLocal, inputContainerRef]),
          className: inputContainer,
          style: { maxWidth },
          children: [
            !iconRight && renderIcon(),
            /* @__PURE__ */ jsx(
              "input",
              {
                onChange: handleChange,
                onKeyDown: handleKeyDown,
                onKeyUp: handleKeyUp,
                name,
                type,
                defaultValue,
                id: inputIdState,
                className: inputClass,
                ref,
                "aria-labelledby": props["aria-labelledby"] ?? void 0,
                "aria-describedby": getAriaDescribedBy(props, errorTextUuid),
                "aria-invalid": !!onError,
                disabled,
                placeholder,
                readOnly,
                autoComplete: autoComplete || "off",
                required,
                autoFocus,
                ...rest
              }
            ),
            iconRight && renderIcon()
          ]
        }
      ),
      /* @__PURE__ */ jsx("div", { className: styles["content-wrapper__right-of-input"], children: rightOfInput })
    ] }),
    maxCharacters && /* @__PURE__ */ jsx(
      MaxCharacters,
      {
        maxCharacters,
        length: input.toString().length,
        maxText,
        onColor,
        maxWidth
      }
    ),
    afterInputChildren
  ] }) });
});
Input.displayName = "Input";
export {
  Input as I,
  InputTypes as a
};
//# sourceMappingURL=Input.js.map
